import { Injectable } from '@angular/core';
import { ApiService } from 'src/app/core/services/api.service';

import AES from 'crypto-js/aes';
import Utf8 from 'crypto-js/enc-utf8';

const FOUR_HOURS_MS = 14400000;

@Injectable()
export class TableService {
    public restaurantInfo = null;

    public currentTable = null;
    public tableItems = [];
    public orderUUID = null;

    constructor(
        private apiService: ApiService,
    ) {}

    setRestaurantInfo(restaurantInfo) {
        this.restaurantInfo = restaurantInfo;
        return this;
    }

    async validateTableLink(): Promise<boolean> {
        const localTable: string | null = JSON.parse(localStorage.getItem(`table-${this.restaurantInfo.id}`));

        const tableFromUrl = this.decodeTableFromUrl(new URLSearchParams(window.location.search).get('t'));

        const orderUUID = localStorage.getItem(`table-uuid-${this.restaurantInfo.id}`);
        this.orderUUID = orderUUID;

        if (orderUUID) {
            const order = await this.apiService.get(`/order/table-uuid/${orderUUID}`).toPromise();

            if (!order || ['CANCELED', 'OVER'].includes(order.status)) {
                this.clearTableOrderSession();

                if (!tableFromUrl) {
                    return false;
                }
            }
        }

        // Entrada pelo QR: vínculo novo, com prazo de validade novo.
        if (tableFromUrl) {
            this.setTable(tableFromUrl);
            this.startTableExpiration();
            return true;
        }

        if (!localTable || this.restaurantInfo.is_closed || !this.restaurantInfo.table_self_service_enabled)
            return false;

        // Comanda aberta feita NESTE dispositivo mantém a sessão de mesa viva.
        if (this.orderUUID) {
            this.currentTable = localTable;
            return true;
        }

        // Sem comanda própria, o vínculo local só vale dentro do prazo. Comanda
        // aberta na mesa por OUTRAS pessoas não é evidência de presença deste
        // dispositivo — revalidar por ela mandava pedido feito de casa para a
        // comanda de quem estava sentado na mesa (OP-1106).
        if (!this.isTableExpirationValid()) {
            this.removeTable();
            this.clearTableExpiration();
            return false;
        }

        this.currentTable = localTable;
        return true;
    }

    /**
     * O vínculo de mesa continua valendo para ENVIAR pedido? Chamar na hora do
     * envio, não só no load: a aba pode ficar aberta por dias depois do QR e o
     * contexto em memória sobrevive a qualquer checagem feita no boot (OP-1106).
     */
    public async isTableSessionStillValid(): Promise<boolean> {
        if (this.orderUUID) {
            return !!(await this.getOpenTableByUUID());
        }

        return this.isTableExpirationValid();
    }

    private isTableExpirationValid(): boolean {
        const expiration = JSON.parse(localStorage.getItem(`tableExp-${this.restaurantInfo.id}`));

        return !!expiration && new Date().getTime() < expiration;
    }

    private startTableExpiration() {
        localStorage.setItem(`tableExp-${this.restaurantInfo.id}`, String(new Date().getTime() + FOUR_HOURS_MS));
    }

	public clearTableExpiration() {
		localStorage.removeItem(`tableExp-${this.restaurantInfo.id}`);
    }

    public setTableOrderUUID(uuid) {
        this.orderUUID = uuid;
		localStorage.setItem(`table-uuid-${this.restaurantInfo.id}`, uuid);
    }

    public invalidateTable() {
		this.currentTable = null;
        this.orderUUID = null;
        this.updateTableLocalStorage();
        localStorage.removeItem(`table-uuid-${this.restaurantInfo.id}`);
    }

    private updateTableLocalStorage() {
        const restaurantId = this.restaurantInfo.id;

        localStorage.setItem(`table-${restaurantId}`, JSON.stringify(this.currentTable));
    }

    public decodeTableFromUrl(url: string): string | null {
        if (!url)
            return null;

		return AES.decrypt(decodeURIComponent(url), `tablesecret-${this.restaurantInfo.id}`).toString(Utf8);
    }

	public setTable(table) {
		this.currentTable = table;
		this.updateTableLocalStorage();
	}

	public removeTable() {
		this.currentTable = null;
		this.updateTableLocalStorage();
	}

    public async getOpenTableByUUID() {
        if (!this.orderUUID)
            return null;

        const order = await this.apiService.get(`/order/table-uuid/${this.orderUUID}`).toPromise();
        if (!order || ['CANCELED', 'OVER'].includes(order.status)) {
            return null
        }
        return order;
    }

    public async isTableClosed(): Promise<boolean> {
        if (!this.orderUUID)
            return false;

        const openTable = await this.getOpenTableByUUID();
        return !openTable;
    }

    public clearTableOrderSession() {
        this.orderUUID = null;
        localStorage.removeItem(`table-uuid-${this.restaurantInfo.id}`);
    }

    public resetTableData() {
        this.orderUUID = null;
        this.removeTable();
        this.clearTableExpiration();
        localStorage.removeItem(`table-uuid-${this.restaurantInfo.id}`);
    }
}
